import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MediaInput } from "../MediaInput";
import { TYPE_OPTIONS, SOURCE_OPTIONS } from "./clientConstants";

const lbl = "text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500";
const blank = { name: "", email: "", phone: "", secondary_phone: "", client_type: "other", company: "", team_name: "", website: "", photo_url: "", logo_url: "", address: { street: "", city: "", state: "", zip: "" }, status: "active", client_source: "manual", internal_notes: "", require_deposit: false };

export function ClientFormSheet({ client, onClose, onSaved }) {
  const isEdit = Boolean(client);
  const [form, setForm] = useState(client ? {
    name: client.name, email: client.email, phone: client.phone || "", secondary_phone: client.secondary_phone || "",
    client_type: client.client_type || "other", company: client.company || "", team_name: client.team_name || "", website: client.website || "",
    photo_url: client.photo_url || "", logo_url: client.logo_url || "",
    address: { street: client.address?.street || "", city: client.address?.city || "", state: client.address?.state || "", zip: client.address?.zip || "" },
    status: client.status || "active", client_source: client.client_source || "manual",
    internal_notes: client.internal_notes || "", require_deposit: Boolean(client.require_deposit),
  } : blank);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setAddr = (patch) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter the client's name"); return; }
    if (!form.email.trim()) { toast.error("Enter the client's email"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.patch(`/admin/clients/${client.id}`, form);
        toast.success("Client updated");
        onSaved(r.data);
      } else {
        const r = await api.post("/admin/clients", form);
        toast.success(`${r.data.name} was added. A password-setup email was sent.`);
        onSaved(r.data);
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent data-testid={isEdit ? "edit-client-sheet" : "add-client-sheet"} className="sm:max-w-[540px] w-full overflow-y-auto rounded-l-2xl">
        <SheetHeader><SheetTitle className="font-serif-display text-3xl font-normal text-[#101828]">{isEdit ? "Edit client" : "Add new client"}</SheetTitle><SheetDescription>{isEdit ? "Update this client's information." : "They'll receive an email to set their own password and access their dashboard."}</SheetDescription></SheetHeader>

        <div className="mt-6 space-y-6 pb-8">
          <Section title="Personal information">
            <Field label="Full name"><Input data-testid="client-form-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className="rounded-lg" /></Field>
            <Field label="Email address"><Input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="rounded-lg" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><Input data-testid="client-form-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="rounded-lg" /></Field>
              <Field label="Secondary phone"><Input data-testid="client-form-secondary-phone" value={form.secondary_phone} onChange={(e) => set({ secondary_phone: e.target.value })} className="rounded-lg" /></Field>
            </div>
          </Section>

          <Section title="Photo & branding">
            <div className="grid grid-cols-2 gap-3">
              <MediaInput label="Profile photo" testid="client-form-photo" value={form.photo_url} onChange={(photo_url) => set({ photo_url })} accept="image/png,image/jpeg,image/webp" />
              <MediaInput label="Brokerage logo" testid="client-form-logo" value={form.logo_url} onChange={(logo_url) => set({ logo_url })} accept="image/png,image/svg+xml,image/webp" />
            </div>
          </Section>

          <Section title="Business information">
            <Field label="Client type">
              <Select value={form.client_type} onValueChange={(v) => set({ client_type: v })}>
                <SelectTrigger data-testid="client-form-type" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Company / brokerage"><Input data-testid="client-form-company" value={form.company} onChange={(e) => set({ company: e.target.value })} className="rounded-lg" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Team name"><Input data-testid="client-form-team" value={form.team_name} onChange={(e) => set({ team_name: e.target.value })} className="rounded-lg" /></Field>
              <Field label="Website"><Input data-testid="client-form-website" value={form.website} onChange={(e) => set({ website: e.target.value })} className="rounded-lg" /></Field>
            </div>
          </Section>

          <Section title="Address (optional)">
            <Field label="Street address"><Input data-testid="client-form-street" value={form.address.street} onChange={(e) => setAddr({ street: e.target.value })} className="rounded-lg" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City"><Input data-testid="client-form-city" value={form.address.city} onChange={(e) => setAddr({ city: e.target.value })} className="rounded-lg" /></Field>
              <Field label="State"><Input data-testid="client-form-state" value={form.address.state} onChange={(e) => setAddr({ state: e.target.value })} className="rounded-lg" /></Field>
              <Field label="ZIP"><Input data-testid="client-form-zip" value={form.address.zip} onChange={(e) => setAddr({ zip: e.target.value })} className="rounded-lg" /></Field>
            </div>
          </Section>

          <Section title="Additional information">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                  <SelectTrigger data-testid="client-form-status" className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem>{client?.status === "archived" && <SelectItem value="archived">Archived</SelectItem>}</SelectContent>
                </Select>
              </Field>
              <Field label="Client source">
                <Select value={form.client_source} onValueChange={(v) => set({ client_source: v })}>
                  <SelectTrigger data-testid="client-form-source" className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Internal notes"><Textarea data-testid="client-form-notes" value={form.internal_notes} onChange={(e) => set({ internal_notes: e.target.value })} className="rounded-lg" rows={3} /></Field>
            <div className="flex items-center justify-between gap-5 border-t border-slate-200 pt-4"><div><Label className={lbl}>Require deposit</Label><p className="mt-1 text-sm text-slate-500">Mark future orders from this client as deposit required.</p></div><Switch data-testid="client-form-require-deposit" checked={form.require_deposit} onCheckedChange={(require_deposit) => set({ require_deposit })} /></div>
          </Section>
        </div>

        <SheetFooter className="sticky bottom-0 bg-background pt-4 border-t border-slate-200">
          <Button data-testid="client-form-cancel" variant="ghost" className="rounded-lg" onClick={onClose}>Cancel</Button>
          <Button data-testid="client-form-save" disabled={saving} className="rounded-lg bg-[#0C2347] hover:bg-[#123262] text-white shadow-[0_3px_8px_rgba(12,35,71,0.13)]" onClick={save}>{saving ? "Saving…" : "Save client"}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return <div className="space-y-3"><div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{title}</div>{children}</div>;
}
function Field({ label, children }) {
  return <div><Label className={lbl}>{label}</Label><div className="mt-1.5">{children}</div></div>;
}
